import { apiGet, apiSend } from "./core.js";

/**
 * Connecting a Spotify account — phase 19c, ADR 0021.
 *
 * Hand-kept, like the rest of the plain-HTTP half: there is no generator for
 * these, so a change in `src/http/spotify.rs` has to be made here too.
 *
 * ⚠️ **There is no `connect()` here on purpose.** Beginning the handshake is a
 * browser *navigation* — `GET /api/spotify/authorize` answers a 303 to Spotify
 * — and fetching it would follow the redirect in the background and throw the
 * page away. The screen sets `window.location` instead.
 */

/** `GET /api/spotify/status` */
export interface SpotifyStatus {
  /**
   * Whether this installation runs the integration at all.
   *
   * Separate from `connected` so a screen can say "this installation does not
   * use Spotify" rather than offering a button that leads nowhere.
   */
  configured: boolean;
  /** Whether this account has a connection. */
  connected: boolean;
  /** What Spotify calls them — so a friend can see they authorised the account
   * they meant to, not a second one they forgot they were signed in to. */
  display_name: string | null;
  /**
   * Whether the stored grant still covers what the app asks for now.
   *
   * `false` means reconnect. Surfaced rather than discovered, because the
   * alternative is a 403 from a playlist read that nobody can interpret.
   */
  scopes_current: boolean;
}

/** What a disconnect answers with. */
export interface SpotifyDisconnected {
  /** Whether there was anything to remove. `false` is not an error. */
  removed: boolean;
  /** Where a friend revokes the grant itself, which sonolume cannot. */
  revoke_at: string;
}

/** Whether this account is connected, and whether the installation even can be. */
export async function spotifyStatus(): Promise<SpotifyStatus> {
  return apiGet<SpotifyStatus>("/spotify/status");
}

/**
 * Where to send the browser to begin the handshake.
 *
 * A path rather than a call: see the module note.
 */
export const SPOTIFY_AUTHORIZE_PATH = "/api/spotify/authorize";

/**
 * Forget the connection.
 *
 * ⚠️ Does **not** revoke the authorisation at Spotify — there is no API for
 * that. The answer carries where a person does it themselves.
 */
export async function disconnectSpotify(): Promise<SpotifyDisconnected> {
  return apiSend<SpotifyDisconnected>("DELETE", "/spotify");
}
